import React from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  LinearProgress,
  Stack,
  Chip,
  Tooltip,
  IconButton,
} from "@mui/material";
import { Info as InfoIcon } from "@mui/icons-material";

const UnifiedAssessmentCard = ({
  member,
  onStartAssessment,
  basicAssessment,
  recommendedAssessment,
  advancedAssessment,
}) => {
  // Calculate completion percentage
  const getCompletionPercentage = () => {
    let completed = 0;
    if (basicAssessment?.assessment_date) completed++;
    if (recommendedAssessment?.assessment_date) completed++;
    if (advancedAssessment?.assessment_date) completed++;
    return (completed / 3) * 100;
  };

  // Get the next incomplete step
  const getNextStep = () => {
    if (!basicAssessment?.assessment_date) return "basic";
    if (!recommendedAssessment?.assessment_date) return "recommended";
    if (!advancedAssessment?.assessment_date) return "advanced";
    return "complete";
  };

  // Get button text based on completion status
  const getButtonText = () => {
    const nextStep = getNextStep();
    if (nextStep === "complete") return "Review Assessment";
    if (nextStep === "basic") return "Start Assessment";
    return "Continue Assessment";
  };

  // Get the current life expectancy projection
  const getCurrentLifeExpectancy = () => {
    if (advancedAssessment?.predicted_life_expectancy_adv) {
      return parseFloat(advancedAssessment.predicted_life_expectancy_adv);
    }
    if (recommendedAssessment?.predictedLifeExpectancy) {
      return parseFloat(recommendedAssessment.predictedLifeExpectancy);
    }
    if (basicAssessment?.calculatedLifeExpectancy) {
      return parseFloat(basicAssessment.calculatedLifeExpectancy);
    }
    return 0; // Return 0 as default if no assessment data is available
  };

  const getStepStatus = (step) => {
    switch (step) {
      case "basic":
        return basicAssessment?.assessment_date ? "Completed" : "Not Started";
      case "recommended":
        return recommendedAssessment?.assessment_date
          ? "Completed"
          : "Not Started";
      case "advanced":
        return advancedAssessment?.assessment_date
          ? "Completed"
          : "Not Started";
      default:
        return "Not Started";
    }
  };

  return (
    <Card sx={{ height: "100%" }}>
      <CardContent>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Health Assessment
            <Tooltip title="Complete all assessment steps for the most accurate life expectancy projection">
              <IconButton size="small" sx={{ ml: 1 }}>
                <InfoIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Typography>

          <LinearProgress
            variant="determinate"
            value={getCompletionPercentage()}
            sx={{ mb: 2, height: 8, borderRadius: 4 }}
          />

          <Stack spacing={1}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography variant="body2">Basic Assessment</Typography>
              <Chip
                size="small"
                label={getStepStatus("basic")}
                color={basicAssessment?.assessment_date ? "success" : "default"}
              />
            </Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography variant="body2">Recommended Assessment</Typography>
              <Chip
                size="small"
                label={getStepStatus("recommended")}
                color={
                  recommendedAssessment?.assessment_date ? "success" : "default"
                }
              />
            </Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography variant="body2">Advanced Assessment</Typography>
              <Chip
                size="small"
                label={getStepStatus("advanced")}
                color={
                  advancedAssessment?.assessment_date ? "success" : "default"
                }
              />
            </Box>
          </Stack>
        </Box>

        {getCurrentLifeExpectancy() !== 0 && (
          <Box
            sx={{
              mb: 3,
              p: 2,
              bgcolor: "primary.light",
              color: "primary.contrastText",
              borderRadius: 1,
              textAlign: "center",
            }}
          >
            <Typography variant="overline" display="block">
              Current Life Expectancy Projection
            </Typography>
            <Typography variant="h4">
              {getCurrentLifeExpectancy().toFixed(1)} years
            </Typography>
            <Typography variant="caption" display="block">
              {getCompletionPercentage() === 100
                ? "Based on comprehensive assessment"
                : "Complete all steps for maximum accuracy"}
            </Typography>
          </Box>
        )}

        <Button
          variant="contained"
          fullWidth
          onClick={() => onStartAssessment(getNextStep())}
          color={getCompletionPercentage() === 100 ? "success" : "primary"}
        >
          {getButtonText()}
        </Button>
      </CardContent>
    </Card>
  );
};

export default UnifiedAssessmentCard;
